import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  timeEntryKeys,
  useDeleteTimeEntry,
  useOwnerProjects,
  useProjectTasks,
  useUpdateTimeEntry,
} from '@/hooks/use-time-entries';
import { extractErrorMessage } from '@/lib/errors';
import { usePermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import type { ProjectTaskOption, TimeEntryItem } from '@hygge/shared';
import { Clock, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  DURATION_REQUIRED,
  editSubmission,
  PICK_DATE_MESSAGE,
  readInstant,
  seedLinkedTime,
  type RowState,
} from '../lib/linked-time-fields';
import { canDeleteEntry } from '../lib/entry-lock';
import { editChangesNothing } from '../lib/edit-entry-changes';
import {
  editTargetChanges,
  projectOptions,
  taskAfterProjectChange,
  taskChoiceLabel,
  taskOptions,
} from '../lib/edit-entry-target';
import { billableBoxText, hoursBoxSeed, readHoursChange } from '../lib/hours-input';
import { FieldError } from './field-error';
import { DeleteEntryConfirm } from './delete-entry-confirm';
import { TimeFieldsRow } from './time-fields-row';

interface EditEntryDialogProps {
  entry: TimeEntryItem;
  onClose: () => void;
  /**
   * Hold the save — and the dialog open on "Saving..." — until the refetch it
   * starts has landed. Show entries asks for it, so nothing opens on its row
   * as it was before the save; off for My Time and a task's time log. [HW-974]
   */
  untilRefetched?: boolean;
}

export function EditEntryDialog({ entry, onClose, untilRefetched = false }: EditEntryDialogProps) {
  const updateMutation = useUpdateTimeEntry({ untilRefetched });
  const deleteMutation = useDeleteTimeEntry();
  const queryClient = useQueryClient();

  /** What the dialog opened on — the row and the Date box, as the wall-clock
   *  digits the entry holds [HW-916]. Pinned, so "has this been touched" has
   *  something to compare with. */
  const [seed] = useState(() => seedLinkedTime(entry));

  const [description, setDescription] = useState(entry.description ?? '');
  const [row, setRow] = useState<RowState>(() => ({
    linked: seed.state,
    draft: null,
    durationTyped: false,
    rejected: null,
  }));
  const timeState = row.linked;
  /**
   * Whether the reader has changed the Duration.
   *
   * Untouched, the entry is saved at exactly its stored length — which carries
   * any sub-minute seconds the minute grammar cannot show (a timer stopped at
   * 12 seconds), so a note edit does not round them away. Touched, what was
   * typed is what is meant. Tracked as "was it touched", not "does it still
   * read the same": typing 1h 1m and back to 1h is a deliberate whole hour.
   */
  const [durationEdited, setDurationEdited] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [date, setDate] = useState(() => seed.dateKey);

  /**
   * Project and task, editable on all three surfaces since HW-974 (call
   * 06.10; HW-960 "modify all fields"). The project list is the entry OWNER's
   * active projects, not the viewer's: an entry stays on its owner's projects
   * (2026-10-09 decision; the server refuses a move elsewhere). The entry's own
   * project is kept in it when that list does not carry it; the task list is
   * the chosen project's open tasks plus the entry's current one while it is
   * selected. [HW-974]
   */
  const { data: ownerProjects } = useOwnerProjects(entry.employeeId);
  const [projectId, setProjectId] = useState(entry.projectId);
  const [taskId, setTaskId] = useState<string | null>(entry.taskId);
  const { data: projectTasks, isLoading: tasksLoading } = useProjectTasks(projectId);
  const projectChoices = projectOptions(ownerProjects ?? [], entry.project);
  const taskChoices = taskOptions(
    projectTasks,
    taskId !== null && taskId === entry.taskId ? entry.task : null,
  );

  /**
   * Billable Hours, for a holder of "Edit any time log" only — the one grant
   * the server lets change them — and only on an entry whose read carried the
   * figure (the lists strip it for everyone else). Read like the Duration box
   * above it (`parseDuration`, zero allowed) and seeded in that grammar's
   * spelling; sent only when changed, so an untouched box leaves the server's
   * "billable follows a changed duration" rule to apply — or, once the holder
   * has typed in it, with any save that changes the duration
   * (`editTargetChanges`). "Touched" is sticky, as `durationEdited` is: typing
   * back to the stored figure still means that figure. Until then, on an
   * unadjusted entry, the box shows a changed Duration's length, which is
   * what the server will store (`billableBoxText`). [HW-974]
   */
  const holdsEditAll = usePermission('time.edit.all');
  const storedBillable = entry.billableDuration;
  const showsBillable = holdsEditAll && storedBillable !== undefined;
  const [billableSeed] = useState(() =>
    storedBillable !== undefined ? hoursBoxSeed(storedBillable) : '',
  );
  const [billableText, setBillableText] = useState(billableSeed);
  const [billableTouched, setBillableTouched] = useState(false);
  // Pinned with the seed: during a held save the entry prop already carries
  // the saved length, and the box must not jump back before the dialog
  // closes. [HW-974]
  const [billableOpened] = useState(() => ({
    adjusted: entry.billableAdjustedAt != null,
    duration: entry.duration,
  }));
  const billableChange =
    showsBillable && storedBillable !== undefined
      ? readHoursChange({
          field: 'billable',
          raw: billableText,
          seeded: billableSeed,
          storedSeconds: storedBillable,
        })
      : ({ kind: 'unchanged' } as const);
  const billableError =
    billableChange.kind === 'error'
      ? billableChange.message
      : billableChange.kind === 'empty'
        ? DURATION_REQUIRED
        : null;

  /**
   * What a save would send, or null when it may not be sent — one rule for the
   * payload and for whether Save is alive, so the button can never be offered
   * for a request the API is bound to refuse. Which length and which instant
   * that comes to is `editEntryTimes`' business, and tested there.
   */
  /** One reading for the button's liveness and for the save — see
   *  `editSubmission`, which settles the row before answering. */
  const submission = editSubmission({
    row,
    stored: entry,
    dateKey: date,
    seedStart: seed.state.start,
    durationEdited,
  });
  const dateIsValid = readInstant(date, timeState.start) !== null;
  // The project, task and Billable Hours a save adds — only what changed.
  // [HW-974]
  const changes = editTargetChanges(entry, {
    projectId,
    taskId,
    billableSeconds:
      billableChange.kind === 'changed'
        ? billableChange.seconds
        : showsBillable && billableChange.kind === 'unchanged'
          ? (storedBillable ?? null)
          : null,
    billableTouched,
    durationChanges: submission.payload !== null && submission.payload.duration !== entry.duration,
  });
  // Off until something changed, and off again when the value is put back:
  // a save that changes nothing is a request for nothing, which the server
  // now answers with a 200 no-op — the button says so first. [HW-973]
  const unchanged = editChangesNothing(entry, submission.payload, description, changes);
  // Untouched on an unadjusted entry, the box shows the length a Save would
  // send — what the server will store — while its own text, the one read
  // above, stays the seed and is not sent. [HW-974]
  const billableShown = billableBoxText({
    adjusted: billableOpened.adjusted,
    touched: billableTouched,
    typed: billableText,
    storedDuration: billableOpened.duration,
    savedDuration: submission.payload?.duration ?? null,
  });
  const canSave = submission.payload !== null && billableError === null && !unchanged;

  /**
   * A refusal from the server names this day and this length — both of which
   * the next keystroke can change — so it goes as soon as the reader acts on
   * it, rather than standing over a form it no longer describes.
   */
  const clearSaveError = () => {
    if (updateMutation.isError) updateMutation.reset();
  };

  /** Every route into the row runs through here, the save included, so a
   *  Duration the settle moves counts as edited exactly as a typed one does. */
  const applyRow = (next: RowState) => {
    clearSaveError();
    if (next.linked.duration !== row.linked.duration) setDurationEdited(true);
    setRow(next);
  };

  /**
   * A new project keeps the task only when it is known to belong there — the
   * server refuses a move that keeps another project's task. The new
   * project's tasks are read from the cache when this dialog has loaded them
   * before; otherwise the task is cleared and the reader picks again. [HW-974]
   */
  const handleProjectChange = (next: string) => {
    clearSaveError();
    const cached = queryClient.getQueryData<ProjectTaskOption[]>(timeEntryKeys.projectTasks(next));
    setTaskId((current) =>
      taskAfterProjectChange({
        taskId: current,
        nextProjectId: next,
        stored: { projectId: entry.projectId, taskId: entry.taskId },
        nextProjectTaskIds: cached ? cached.map((t) => t.id) : null,
      }),
    );
    setProjectId(next);
  };

  /** Still on screen when the answer arrives? See ManualEntryForm. */
  const mounted = useRef(true);
  useEffect(() => {
    // Set on the way IN as well as cleared on the way out. StrictMode mounts an
    // effect, unmounts it and mounts it again, so a ref only cleared by the
    // cleanup stays false for the rest of the dialog's life — and this one
    // guards the `onClose` on success, which is how a successful save stopped
    // closing the dialog in the dev build while the production one was fine.
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleSave = () => {
    // Read once, through the same function the button judged by — it settles the
    // box still being typed in, which a click does not do on Safari or on
    // Firefox for macOS. The settled row goes back on screen either way, and a
    // settle that moved the Duration makes it the reader's own.
    const { row: settled, payload, durationEdited: edited } = submission;
    if (settled !== row) applyRow(settled);
    if (edited !== durationEdited) setDurationEdited(true);
    if (!payload || billableError !== null || editChangesNothing(entry, payload, description, changes)) {
      return;
    }

    // `mutateAsync` so the answer survives this dialog — see ManualEntryForm.
    updateMutation
      .mutateAsync({
        id: entry.id,
        description: description || null,
        ...payload,
        ...changes,
      })
      .then(() => {
        if (mounted.current) onClose();
      })
      .catch((error: unknown) => {
        if (!mounted.current) {
          toast.error(extractErrorMessage(error, 'This entry could not be saved.'));
        }
      });
  };

  // Confirmed, not clicked: the trash button only opens the question. The dialog
  // closes on success alone — a failed delete leaves both dialogs standing, with
  // the global error toast saying why. [HW-843]
  const handleDelete = () => {
    deleteMutation.mutate(entry.id, {
      onSuccess: () => {
        setConfirmingDelete(false);
        onClose();
      },
    });
  };

  // Closable at any time, mid-save included — see ManualEntryForm for why that
  // is safe now and why holding it shut was worse. [HW-859]
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[380px] p-0 gap-0 overflow-x-hidden">
        <div className="px-5 pt-5 pb-4">
          <DialogHeader className="space-y-1.5">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-hygge/10 flex items-center justify-center">
                <Clock className="h-4 w-4 text-hygge" />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-base">Edit Time Entry</DialogTitle>
                <DialogDescription className="text-xs mt-0.5">
                  Update the project, task, date, times or note
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
        </div>

        <div className="px-5 pb-5 space-y-4">
          {/* Project — the entry's own stays listed when the active list does
              not carry it, dot greyed like Log Time's. [HW-974] */}
          <div className="space-y-1.5">
            <label htmlFor="edit-project" className="text-xs font-medium text-muted-foreground">Project</label>
            <Select value={projectId} onValueChange={handleProjectChange}>
              <SelectTrigger id="edit-project" className="h-9 text-sm">
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                {projectChoices.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    <div className="flex items-center gap-2">
                      <div
                        className={cn('h-2 w-2 rounded-full shrink-0', !p.offered && 'bg-muted-foreground/40')}
                        style={p.offered && p.color ? { backgroundColor: p.color } : undefined}
                      />
                      <span className="truncate">{p.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Task — a searchable combobox (key and title): "No task", the
              chosen project's open tasks on the boards this reader can open,
              and the entry's current one while it is selected. `modal`, or the
              list cannot be wheel-scrolled inside this Dialog (the Dialog's
              scroll lock swallows it — HW-677). [HW-974] */}
          <div className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Task</span>
            <SearchableSelect
              modal
              ariaLabel="Task"
              value={taskId ?? ''}
              onChange={(value) => {
                clearSaveError();
                setTaskId(value === '' ? null : value);
              }}
              options={taskChoices.map((t) => ({ id: t.id, name: taskChoiceLabel(t) }))}
              allowNone
              noneLabel="No task"
              placeholder={tasksLoading ? 'Loading tasks…' : 'No task'}
              searchPlaceholder="Search by key or title"
              emptyText="No matching task"
              className="text-sm"
            />
          </div>

          {/* Date */}
          <div className="pb-1">
            <div className="relative">
              <div className="space-y-1.5">
                <label htmlFor="edit-date" className="text-xs font-medium text-muted-foreground">Date</label>
                <Input
                  id="edit-date"
                  type="date"
                  value={date}
                  onChange={(e) => {
                    clearSaveError();
                    setDate(e.target.value);
                  }}
                  className="h-9 text-sm"
                />
              </div>
              {!dateIsValid && <FieldError>{PICK_DATE_MESSAGE}</FieldError>}
            </div>
          </div>

          {/* Duration | Start | End, linked and shared with Log Time. */}
          <TimeFieldsRow row={row} onChange={applyRow} idPrefix="edit" dense />

          {/* Billable Hours — "Edit any time log" only. [HW-974] */}
          {showsBillable && (
            <div className="pb-1">
              <div className="relative">
                <div className="space-y-1.5">
                  <label htmlFor="edit-billable" className="text-xs font-medium text-muted-foreground">
                    Billable Hours
                  </label>
                  <Input
                    id="edit-billable"
                    value={billableShown}
                    placeholder="e.g. 2h 30m"
                    onChange={(e) => {
                      clearSaveError();
                      setBillableTouched(true);
                      setBillableText(e.target.value);
                    }}
                    className="h-9 text-sm"
                  />
                </div>
                {billableError && <FieldError>{billableError}</FieldError>}
              </div>
            </div>
          )}

          {/* Description */}
          <div className="space-y-1.5">
            <label htmlFor="edit-description" className="text-xs font-medium text-muted-foreground">Description</label>
            <Textarea
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="text-sm resize-none"
              placeholder="What did you work on?"
            />
          </div>

          {/* Why nothing happened, where the button that did nothing is. In the
              flow rather than out of it, unlike the field messages above: this
              one arrives on a press rather than on a keystroke, and it is a
              whole sentence from the server — a 220px strip over the buttons
              would cover them. [HW-859] */}
          {updateMutation.isError && (
            <p
              role="alert"
              className="rounded-md bg-destructive/5 px-3 py-2 text-xs leading-snug text-destructive"
            >
              {extractErrorMessage(
                updateMutation.error,
                'This entry could not be saved. Please try again.',
              )}
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            {/* Not while a save is in flight: the delete would win, close this
                dialog, and the PATCH still on its way would come back 404 —
                answered, now that a save reports from its promise, with a toast
                saying "Time entry not found" about the row just deleted on
                purpose. Two writes on one row, in a race nobody asked for. */}
            {/* Only when the server said the row may be deleted: the dialog
                opens from the timesheet, a task's time log and Show entries
                alike, and since HW-972 a holder of "Edit any time log" alone
                reaches somebody else's row here with no right to remove it. */}
            {canDeleteEntry(entry) && (
              <button
                onClick={() => setConfirmingDelete(true)}
                disabled={deleteMutation.isPending || updateMutation.isPending}
                className="h-9 w-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors cursor-pointer disabled:opacity-50"
                title="Delete entry"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
            <div className="flex-1" />
            <Button variant="outline" size="sm" className="h-9" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-9"
              onClick={handleSave}
              disabled={updateMutation.isPending || !canSave}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>

      <DeleteEntryConfirm
        entry={confirmingDelete ? entry : null}
        isPending={deleteMutation.isPending}
        onCancel={() => setConfirmingDelete(false)}
        onConfirm={handleDelete}
      />
    </Dialog>
  );
}
